import type { ReactNode } from "react";
import { Pressable, type PressableProps, Text, View } from "react-native";

import { cn } from "@/lib/ui/cn";
import { slopToFloor } from "@/lib/ui/touchTarget";

/** The pill's painted height (`min-h-9`), for the slop arithmetic. */
const PILL_HEIGHT = 36;

/**
 * Renders an inventory filter pill: a single-select toggle for one value of one taxonomy, tinted
 * when it is the active choice.
 *
 * The inventory rail, the filter sheet and Activity's ledger filter share this component so their
 * geometry and accessibility state cannot drift apart.
 *
 * The pill paints 36pt instead of relying on `hitSlop` around a shorter paint. A wrapping row's gap
 * is 7pt, so slop past about 4pt a side crosses into the neighbouring row, where React Native
 * hit-tests whichever view is on top and a tap near the edge lands on the wrong filter.
 * 36 + 4 + 4 is exactly the 44pt iOS floor.
 *
 * `min-h-9` is 36pt because `tailwind.config.js` puts the box-dimension scale in points. Spacing
 * such as the wrapped row's `gap-2` is still rem, which NativeWind resolves at 14, so that gap is
 * 7pt rather than 8.
 *
 * `py-1.5` stays under `min-h-9`, so 36 is a minimum, not a fixed height: at large Dynamic Type the
 * label grows the pill instead of clipping inside it.
 */
export function FilterPill({
  label,
  active,
  onPress,
  className,
  trailing,
  a11yLabel,
  disabled,
  testID,
  // 36pt of paint plus whatever reaches the platform floor: 4 a side on iOS (44), 6 on Android
  // (48). A caller with more room around the pill can pass a larger slop.
  hitSlop = slopToFloor(PILL_HEIGHT),
}: {
  label: string;
  active: boolean;
  onPress: () => void;
  className?: string;
  /**
   * A glyph rendered after the label, such as the sort pills' direction arrow.
   *
   * It shares the label's row, so the pill stays one 36pt target. The glyph is decorative to a
   * screen reader, so a pill whose glyph changes its meaning must say so in `a11yLabel`.
   */
  trailing?: ReactNode;
  /** The spoken name, when the visible label alone is not the whole choice ("Name, A to Z"). */
  a11yLabel?: string;
  /**
   * A choice this build cannot offer, such as the labels screen's NIIMBOT target without the
   * printer module. The pill is dimmed and inert rather than hidden, so the option stays visible.
   */
  disabled?: boolean;
  /** Names the pill for a harness rung; the three surfaces share this one selector. */
  testID?: string;
  hitSlop?: PressableProps["hitSlop"];
}) {
  return (
    <Pressable
      onPress={onPress}
      hitSlop={hitSlop}
      disabled={disabled}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel ?? label}
      // Both halves of the selected state, per mobile/AGENTS.md: react-native-web drops
      // `accessibilityState`, and RN core leaves `aria-pressed` alone (it maps only `aria-busy`,
      // `-checked`, `-disabled`, `-expanded` and `-selected`), so neither prop shadows the other.
      aria-pressed={active}
      accessibilityState={{ selected: active, disabled: !!disabled }}
      className={cn(
        "workspace-filter",
        "min-h-9 justify-center rounded-full border px-3 py-1.5",
        active ? "border-primary bg-accent-soft" : "border-border bg-surface",
        disabled && "opacity-50",
        className,
      )}
    >
      <View className="flex-row items-center gap-1.5">
        <Text
          numberOfLines={1}
          ellipsizeMode="tail"
          className={cn(
            "font-label text-xs",
            // `text-primary` on `bg-accent-soft` fails AA at this size (4.43:1, TRO-1375);
            // `text-foreground` clears it by a wide margin. The border and background wash still
            // carry the active tint.
            active ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {label}
        </Text>
        {trailing}
      </View>
    </Pressable>
  );
}
